import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Pill, type PillTone } from '../components/PillGroup.js';
import { Icon } from '../components/Icon.js';
import { DetailSection } from '../components/ui/DetailSection.js';
import { Card, CardTitle } from '../components/ui/Card.js';
import { Select } from '../components/ui/Select.js';
import { SectionHeaderAction } from '../components/ui/SectionHeaderAction.js';
import { ProjectInfoEditModal, LANGUAGE_OPTIONS } from '../components/projects/ProjectInfoEditModal.js';
import { KVRow } from '../components/ui/KVRow.js';
import { KVGrid } from '../components/ui/KVGrid.js';
import { Tabs } from '../components/ui/Tabs.js';
import { Button } from '../components/ui/Button.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { useProject, useProjects, useProjectRevisions, useUpdateProject, type ContentPlan } from '../hooks/useProjects.js';
import { useLead } from '../hooks/useLeads.js';
import { useReports, type ReportRow } from '../hooks/useReports.js';
import { parseServerDate } from '../lib/date.js';
import { useToast } from '../components/ui/Toast.js';
import { BriefSection } from '../components/projects/BriefSection.js';
import { ContentPlanSection } from '../components/projects/ContentPlanSection.js';
import { ProjectClaudePanel } from '../components/projects/ProjectClaudePanel.js';
import { TemplateGallery } from '../components/projects/TemplateGallery.js';
import { GeneratePagePrompt } from '../components/projects/GeneratePagePrompt.js';
import { PagePreview } from '../components/projects/PagePreview.js';
import { ApproveProjectModal } from '../components/projects/ApproveProjectModal.js';
import { RevisionsLedger } from '../components/projects/RevisionsLedger.js';
import { DeliveredView } from '../components/projects/DeliveredView.js';
import { AuditCard } from '../components/projects/AuditCard.js';
import { ProjectOutreachTab } from '../components/projects/ProjectOutreachTab.js';
import { DesignReviewSection } from '../components/projects/DesignReviewSection.js';
import { EstimatesSection } from '../components/projects/EstimatesSection.js';

const STATUS_TONES: Record<string, PillTone> = {
  draft:            'neutral',
  briefed:          'blue',
  content_planning: 'cyan',
  in_design:        'cyan',
  client_review: 'amber',
  revisions:     'warn',
  approved:      'success',
  delivered:     'info',
  archived:      'muted',
};

const DESIGN_ENABLED = new Set([
  'briefed', 'content_planning', 'in_design', 'client_review', 'revisions', 'approved', 'delivered', 'archived',
]);

type TabValue = 'info' | 'brief' | 'contentPlan' | 'reports' | 'design' | 'outreach' | 'estimates';

function parseContentPlan(raw: string | null | undefined): ContentPlan | null {
  if (!raw) return null;
  try { return JSON.parse(raw) as ContentPlan; } catch { return null; }
}

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('es', { month: 'long', day: 'numeric', year: 'numeric' });
}

function ProjectReportRow({ report, onView }: { report: ReportRow; onView: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      onClick={onView}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onView(); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)',
        padding: 'var(--evo-space-3) var(--evo-space-4)',
        background: 'var(--evo-bg-2)',
        borderRadius: 'var(--evo-r-sm)',
        border: '1px solid var(--evo-stroke)',
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--evo-fs-body)', fontWeight: 'var(--evo-fw-medium)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {report.title}
        </div>
        <div style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', marginTop: '2px' }}>
          {t('projects.reports.createdAt')} {parseServerDate(report.createdAt).toLocaleDateString('es', { month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
      </div>
      <Pill tone="neutral" variant="subtle" size="sm">
        {report.type.replace(/_/g, ' ')}
      </Pill>
      <Icon name="arrow-right" size={12} />
    </div>
  );
}

export function ProjectDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { setOverride } = useTopBarCtx();
  const toast = useToast();
  const { data: project, isLoading } = useProject(slug);
  const { data: originLead } = useLead(project?.leadId ?? null);
  const { data: reportsData, isLoading: reportsLoading } = useReports(
    project ? { projectId: project.id } : {},
  );
  const [activeTab, setActiveTab] = useState<TabValue>('info');
  const [plan, setPlan] = useState<ContentPlan | null>(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const { data: revisionsData } = useProjectRevisions(project?.id);
  const nextRevisionNumber = (revisionsData?.length ?? 0) + 1;
  const updateProject = useUpdateProject();

  useEffect(() => {
    setPlan(parseContentPlan(project?.contentPlan));
  }, [project?.contentPlan]);

  // Navigation: prefer state passed from list; fall back to full project list for direct URL entry
  const locState = (location.state as { filteredIds?: string[]; filters?: Record<string, unknown> } | null) ?? {};
  const stateIds: string[] = locState.filteredIds ?? [];
  const { data: allProjectsData } = useProjects();
  const fallbackIds = (allProjectsData?.items ?? []).map(p => p.slug);
  const filteredIds = stateIds.length > 0 ? stateIds : fallbackIds;
  const currentIndex = slug ? filteredIds.indexOf(slug) : -1;
  const prevSlug = currentIndex > 0 ? filteredIds[currentIndex - 1] : null;
  const nextSlug = currentIndex < filteredIds.length - 1 ? filteredIds[currentIndex + 1] : null;
  const showNav = filteredIds.length > 1 && currentIndex >= 0;

  function navTo(targetSlug: string) {
    navigate(`/proyectos/${targetSlug}`, { state: location.state });
  }

  useEffect(() => {
    const typePart = project
      ? t(`projects.type.${project.type}`, { defaultValue: project.type })
      : '';
    const subtypePart = project?.subtype
      ? ` · ${t(`projects.subtype.${project.subtype}`, { defaultValue: project.subtype })}`
      : '';
    const subtitle = typePart + subtypePart;

    setOverride({
      title: project?.name ?? t('projects.title'),
      heroIcon: 'projects-large',
      heroIconColor: 'var(--evo-steel)',
      ...(subtitle ? { subtitle } : {}),
    });
    return () => setOverride({});
  }, [setOverride, project, t]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <LoadingState fullHeight />
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <EmptyState title={t('common.notFound', { defaultValue: 'No encontrado' })} />
      </div>
    );
  }

  const statusTone = STATUS_TONES[project.status] ?? 'neutral';
  const projectReports = reportsData?.reports ?? [];

  const tabs = [
    { value: 'info' as const,    label: t('projects.tabs.info') },
    { value: 'brief' as const,   label: t('projects.tabs.brief') },
    {
      value: 'contentPlan' as const,
      label: t('projects.tabs.contentPlan'),
      ...(project.status === 'draft' ? { disabled: true, tooltip: t('projects.tabs.disabledContentPlan') } : {}),
    },
    { value: 'reports' as const, label: t('projects.tabs.reports') },
    {
      value: 'design' as const,
      label: t('projects.tabs.design'),
      ...(!DESIGN_ENABLED.has(project.status) ? { disabled: true, tooltip: t('projects.tabs.disabledDesign') } : {}),
    },
    { value: 'outreach' as const, label: t('projects.tabs.outreach') },
    { value: 'estimates' as const, label: t('projects.tabs.estimates') },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* SubNav + Tabs — merged single row */}
      <div style={{
        height: 'var(--evo-subnav-h)',
        borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0,
        background: 'var(--evo-bg)',
      }}>
        <div style={{ padding: '0 var(--evo-content-pad-x)', height: '100%' }}>
        <div style={{ maxWidth: '1200px', height: '100%', display: 'flex', alignItems: 'stretch' }}>

        {/* Left: back + separator + status pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, paddingRight: '16px' }}>
          <Button
            variant="ghost"
            tone="muted"
            size="sm"
            icon={<span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><Icon name="arrow-right" size={12} /></span>}
            onClick={() => navigate('/proyectos')}
          >
            {t('projects.detail.back')}
          </Button>
          <div style={{ width: '1px', height: '16px', background: 'var(--evo-stroke)', flexShrink: 0 }} />
          <Pill tone={statusTone} variant="outline" size="sm">
            {t(`projects.status.${project.status}`, { defaultValue: project.status })}
          </Pill>
        </div>

        {/* Tabs: fill remaining space, centered horizontally */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', overflow: 'hidden', minWidth: 0 }}>
          <Tabs<TabValue>
            tabs={tabs}
            value={activeTab}
            onChange={setActiveTab}
            noBorder
          />
        </div>

        {/* Right: Prev/Next navigation */}
        {showNav ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, paddingLeft: 'var(--evo-content-pad-x)' }}>
            <Button
              variant="outline"
              tone="muted"
              size="sm"
              disabled={!prevSlug}
              icon={<span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><Icon name="arrow-right" size={11} /></span>}
              onClick={() => prevSlug && navTo(prevSlug)}
            >
              {t('projects.detail.prev')}
            </Button>
            <span style={{
              fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)',
              color: 'var(--evo-fg-3)', whiteSpace: 'nowrap',
              minWidth: '52px', textAlign: 'center',
            }}>
              {t('projects.detail.navPosition', { current: currentIndex + 1, total: filteredIds.length })}
            </span>
            <Button
              variant="outline"
              tone="muted"
              size="sm"
              disabled={!nextSlug}
              iconAfter={<Icon name="arrow-right" size={11} />}
              onClick={() => nextSlug && navTo(nextSlug)}
            >
              {t('projects.detail.next')}
            </Button>
          </div>
        ) : null}

        </div>
        </div>
      </div>

      {/* Main row: left (content) + right (Claude panel) */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left: scrollable content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Scrollable content */}
          <div style={{ flex: 1, overflowY: 'auto' }}>

            {/* ── Tab: Información ── */}
            {activeTab === 'info' && (
              <div style={{ padding: '0 var(--evo-content-pad-x)' }}>
              <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
                <DetailSection
                  title={t('projects.detail.info')}
                  divider={false}
                  titleRight={
                    <SectionHeaderAction
                      onClick={() => setEditInfoOpen(true)}
                    >
                      {t('common.edit')}
                    </SectionHeaderAction>
                  }
                >
                  <div className="evo-info-grid">

                    {/* Left — Project Details */}
                    <Card header={<CardTitle>{t('projects.detail.cardDetails')}</CardTitle>} padding="sm">
                      <KVGrid columns={1}>
                        <KVRow label={t('projects.columns.type')} value={t(`projects.type.${project.type}`, { defaultValue: project.type })} />
                        <KVRow label={t('projects.columns.subtype')} value={project.subtype ? t(`projects.subtype.${project.subtype}`, { defaultValue: project.subtype }) : '—'} />
                        <KVRow
                          label={t('projects.fields.language')}
                          value={project.language}
                          renderValue={() => (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                              <Select
                                value={project.language}
                                onChange={v => { if (v) updateProject.mutate({ id: project.id, language: v }); }}
                                options={LANGUAGE_OPTIONS}
                              />
                              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
                                {t('projects.fields.languageHint')}
                              </span>
                            </div>
                          )}
                        />
                        <KVRow label={t('projects.columns.deadline')} value={project.desiredDeadline ? new Date(project.desiredDeadline).toLocaleDateString('es', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'} last />
                      </KVGrid>
                    </Card>

                    {/* Right — Origin */}
                    <Card header={<CardTitle>{t('projects.detail.cardOrigin')}</CardTitle>} padding="sm">
                      <KVGrid columns={1}>
                        <KVRow label={t('projects.columns.contact')} value={project.contactDisplayName ?? String(project.contactId)} />
                        <KVRow label={t('projects.columns.slug')} value={project.slug} />
                        <KVRow label={t('projects.columns.created')} value={fmtDate(project.createdAt)} {...(!project.leadId ? { last: true } : {})} />
                        {project.leadId && (
                          <KVRow
                            label={t('projects.detail.leadOrigin')}
                            value={originLead?.businessName ?? project.leadId}
                            renderValue={() => (
                              <Button
                                variant="ghost"
                                tone="cyan"
                                size="sm"
                                onClick={() => navigate(`/leads/${project.leadId}`)}
                              >
                                {originLead?.businessName ?? project.leadId} →
                              </Button>
                            )}
                            last
                          />
                        )}
                      </KVGrid>
                    </Card>

                  </div>
                </DetailSection>
              </div>
              </div>
            )}

            {/* ── Tab: Brief ── */}
            {activeTab === 'brief' && (
              <div style={{ padding: '0 var(--evo-content-pad-x)' }}>
              <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
                <BriefSection
                  project={project}
                  divider={false}
                  onGenerated={() => setActiveTab('contentPlan')}
                  onSendBrief={() => setActiveTab('outreach')}
                />
              </div>
              </div>
            )}

            {/* ── Tab: Plan de contenido ── */}
            {activeTab === 'contentPlan' && (
              <div style={{ padding: '0 var(--evo-content-pad-x)' }}>
              <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
                <ContentPlanSection
                  project={project}
                  plan={plan}
                  onPlanChange={setPlan}
                  divider={false}
                  onDesignStarted={() => setActiveTab('design')}
                  onReportGenerated={() => setActiveTab('reports')}
                  onSendContentPlan={() => setActiveTab('outreach')}
                />
              </div>
              </div>
            )}

            {/* ── Tab: Reportes ── */}
            {activeTab === 'reports' && (
              <div style={{ padding: '0 var(--evo-content-pad-x)' }}>
              <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
                <DetailSection
                  title={t('projects.reports.title')}
                  divider={false}
                  titleRight={
                    <SectionHeaderAction
                      onClick={() => toast.success('Próximamente')}
                    >
                      {t('projects.reports.create')}
                    </SectionHeaderAction>
                  }
                >
                  {reportsLoading ? (
                    <div style={{ padding: 'var(--evo-space-4) 0' }}>
                      <LoadingState size="sm" />
                    </div>
                  ) : projectReports.length === 0 ? (
                    <EmptyState title={t('projects.reports.empty')} />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
                      {projectReports.map(r => (
                        <ProjectReportRow
                          key={r.id}
                          report={r}
                          onView={() => navigate(`/reports/${r.slug}`)}
                        />
                      ))}
                    </div>
                  )}
                </DetailSection>
              </div>
              </div>
            )}

            {/* ── Tab: Diseño ── */}
            {activeTab === 'design' && (
              <>
                {!DESIGN_ENABLED.has(project.status) ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px', padding: '32px var(--evo-content-pad-x)' }}>
                    <EmptyState title={t('projects.design.lockedMessage')} />
                  </div>
                ) : !project.templateSlug ? (
                  <div style={{ padding: '32px var(--evo-content-pad-x)', maxWidth: '1200px' }}>
                    <TemplateGallery projectId={project.id} selectedSlug={project.templateSlug ?? undefined} />
                  </div>
                ) : project.status === 'delivered' ? (
                  // ── Delivered: currentVersion is always 0 here (cleared by /approve
                  // on delivery) — MUST be checked before the currentVersion===0 branch
                  // below, or a delivered project falls into "start a fresh design"
                  // instead of the ledger + resume ("New revision") button. ──
                  <>
                    <DeliveredView project={project} />
                    <AuditCard projectId={project.id} />
                  </>
                ) : project.currentVersion === 0 ? (
                  <div style={{ padding: '32px var(--evo-content-pad-x)', maxWidth: '1200px' }}>
                    <GeneratePagePrompt project={project} />
                  </div>
                ) : !['in_design', 'revisions', 'client_review'].includes(project.status) ? (
                  // ── Orphan state: page generated but status not in approvable set ──
                  // Prevents showing Approve button when backend would reject (e.g. content_planning + currentVersion>0).
                  <div style={{ padding: '32px var(--evo-content-pad-x)', maxWidth: '1200px' }}>
                    <p style={{
                      fontSize: 'var(--evo-fs-body)',
                      color: 'var(--evo-fg-3)',
                      fontFamily: 'var(--evo-font)',
                    }}>
                      {t('projects.detail.unavailableForStatus', { status: project.status })}
                    </p>
                  </div>
                ) : (
                  // ── Active design (in_design | revisions | client_review) ──
                  <>
                    <PagePreview project={project} />

                    <DesignReviewSection project={project} />

                    {/* Approve button — positioned below the preview panel */}
                    <div style={{
                      padding: 'var(--evo-space-3) var(--evo-content-pad-x)',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      borderTop: '1px solid var(--evo-stroke)',
                    }}>
                      <Button
                        variant="outline"
                        tone="muted"
                        size="sm"
                        onClick={() => setIsApproveModalOpen(true)}
                      >
                        {t('projects.approve.button')}
                      </Button>
                    </div>

                    {/* Revisions ledger — renders nothing if no previous revisions */}
                    <RevisionsLedger projectId={project.id} />

                    {/* ADA/SEO audit card */}
                    <AuditCard projectId={project.id} />
                  </>
                )}

                {/* Approve modal — rendered at this level so it escapes the flex column */}
                {isApproveModalOpen && (
                  <ApproveProjectModal
                    projectId={project.id}
                    revisionNumber={nextRevisionNumber}
                    currentVersion={project.currentVersion}
                    onClose={() => setIsApproveModalOpen(false)}
                    onApproved={() => setIsApproveModalOpen(false)}
                  />
                )}
              </>
            )}

            {/* ── Tab: Outreach ── */}
            {activeTab === 'outreach' && (
              <div style={{ padding: '0 var(--evo-content-pad-x)' }}>
                <ProjectOutreachTab project={project} />
              </div>
            )}

            {activeTab === 'estimates' && (
              <div style={{ padding: '0 var(--evo-content-pad-x)' }}>
              <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
                <EstimatesSection projectId={project.id} contactId={project.contactId} />
              </div>
              </div>
            )}

          </div>

        </div>

        {/* Right: Claude panel */}
        <ProjectClaudePanel project={project} plan={plan} onPlanChange={setPlan} section={activeTab} />

      </div>

      {editInfoOpen && (
        <ProjectInfoEditModal
          project={project}
          open={editInfoOpen}
          onClose={() => setEditInfoOpen(false)}
        />
      )}
    </div>
  );
}
